import React, { useRef, useState, useEffect, useMemo } from "react";
import { useTheme } from '../../theme';
import {
  Clipboard,
  Dimensions,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useToast } from "../../components/LhToastContainer";
import { createApiClient } from "../../api/apiClient";
import { AppDispatch, RootState } from "../../store";
import { prePopulateSubmitTicket, setSelectedTicketId, setPendingAccessRequestTicketId, TicketStatus, TicketUrgency } from "../../store/slices/ticketingPortalSlice";
import { useDispatch, useSelector } from "react-redux";
import styles from "./TicketDetailScreenStyles";
import {
  normalizeTicketStatusKey,
  normalizeUrgencyKey,
  toTitleCaseWord,
} from "../../utils/ticketNormalization";
import {
  STATUS_TEXT,
  STATUS_BG,
  STATUS_COLOR,
  URGENCY_BG,
  URGENCY_COLOR,
} from "../../constants/ticketStatusConstants";
import {
  TICKETING_REOPEN_ELIGIBLE_STATUSES,
  TICKETING_REOPEN_WINDOW_DAYS,
} from "../../constants";
import { useTicketDetail } from "./hooks/useTicketDetail";
import useTicketingAccess from "./hooks/useTicketingAccess";
import { useTicketViewAccess } from "./hooks/useTicketViewAccess";
import TicketAccessDenied from "./components/TicketAccessDenied";
import { useServiceNowEnabled } from "../../hooks/useServiceNowEnabled";
import TicketDetailHeader from "./components/TicketDetailHeader";
import {
  TicketHero,
  TriageCard,
  TicketInfoSection,
  AffectedLocationsSection,
  NotesSection,
  ActivityStream,
  MergedTicketsSection,
  AbsorbedTicketSummary,
  AccessRequestTriageCard,
  AccessRequestDetailSection,
  AccessRequestSkeletonDetail,
  AccessRequestHeroSkeleton,
} from "./components/TicketDetailComponents";
import TicketDetailActionModals from "./components/TicketDetailActionModals";
import ServiceNowIncidentModal from "./components/ServiceNowIncidentModal";
import { CloseTicketModal } from "./components/CloseTicketModal";
import { fetchSubmitTicketFormConfigAndFields } from "../../store/thunks/submitTicketThunk";
import { isCategoryConfigStale } from "../../utils/ticketingCategoryBeta";
import ServiceNowErrorBanner from "./components/ServiceNowErrorBanner";
import { LoadImpact, TicketLevel, UrgencyLevel } from "../../store/slices/ticketingPortalSlice";
import { fetchTicketDetail, fetchSustainTeamMembers, triageAccessRequest, resolveAccessRequestByIT, uploadTicketImage } from "../../store/thunks/ticketingPortalThunk";
import { formatUserName } from "../../utils/emailTouserName";
import { formatDateWithTimezone } from "./util/dateFormatter";
import { ServiceNowIncidentDraft, ServiceNowIncidentStatus } from "../../types/servicenow";

// ── Local Type & Helper Definitions ──────────────────────────────────────────
type MobileTab = "Details" | "Impact" | "Notes" | "Activity";

type ServiceNowLogEntry = {
  id: number;
  operation: string;
  status: string;
  retryCount: number;
  errorCode?: string | null;
  errorMessage?: string | null;
  createdAt?: string | null;
};

// Use shared constants with local aliases
const S_TEXT = STATUS_TEXT;
const S_BG = STATUS_BG;
const S_COLOR = STATUS_COLOR;
const U_BG = URGENCY_BG;
const U_COLOR = URGENCY_COLOR;

const MOBILE_TABS: MobileTab[] = ["Details", "Impact", "Notes", "Activity"];

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: "new", label: "New" }, { value: "progress", label: "In Progress" },
  { value: "waiting-it", label: "Escalated to IT" }, { value: "waiting-tmv", label: "Escalated to TMV" },
  { value: "submitted-ado", label: "Submitted to ADO" }, { value: "waiting-user", label: "Waiting on User" },
  { value: "resolved", label: "Closed" },
];
const URGENCY_OPTIONS: { value: TicketUrgency; label: string }[] = [
  { value: "critical", label: "Critical" }, { value: "high", label: "High" },
  { value: "medium", label: "Medium" }, { value: "low", label: "Low" },
];
const RESOLUTION_PATH_MOBILE = ["Requires IT", "Internal Resolution"];
const ASSIGNED_TO_OPTIONS = ["Unassigned", "Maria Chen (me)", "Derek Park", "Sam Torres", "Jordan Lee"];
const ACT_AVG_BG: Record<string, string> = {
  user: "#005CBC", system: "#656565", sustain: "#7871E2", servicenow: "#0078A7",
};

// Helper function to get dynamic label for market/level reference based on ticketLevel
const getLevelReferenceLabel = (ticketLevel?: string): string => {
  switch (ticketLevel?.toLowerCase()) {
    case "location":
      return "location";
    case "region":
      return "region";
    case "market":
    default:
      return "market";
  }
};

type Props = { onNavigate: (screen: string) => void; ticketId?: string; isDeepLinked?: boolean };

const TicketDetailScreen: React.FC<Props> = ({ onNavigate, ticketId, isDeepLinked = false }) => {
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  /* ── State ── */
  const [activeTab, setActiveTab] = useState<MobileTab>("Details");
  const [showAllLocs, setShowAllLocs] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  // Unified dropdown overlay state
  const [openDropdown, setOpenDropdown] = useState<"status" | "urgency" | "assigned" | "team" | null>(null);
  const [dropPos, setDropPos] = useState({ x: 0, y: 0, width: 0 });
  // Re-Open confirmation modal
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  // Assigned Team reassignment confirmation modal (mobile layout)
  const [pendingTeam, setPendingTeam] = useState<{ id: number; name: string } | null>(null);
  // Merged banner visibility state - persists until user closes it
  const [showMergedBanner, setShowMergedBanner] = useState(true);
  // Track affected locations Y position for web scrolling
  const [affectedLocationsY, setAffectedLocationsY] = useState(0);
  const [showServiceNowModal, setShowServiceNowModal] = useState(false);
  const [showCloseTicketModal, setShowCloseTicketModal] = useState(false);
  const [isServiceNowSubmitting, setIsServiceNowSubmitting] = useState(false);
  const [serviceNowStatus, setServiceNowStatus] = useState<ServiceNowIncidentStatus | null>(null);
  const [isServiceNowRetrying, setIsServiceNowRetrying] = useState(false);
  const [serviceNowLogs, setServiceNowLogs] = useState<ServiceNowLogEntry[]>([]);
  const [serviceNowLogsLoading, setServiceNowLogsLoading] = useState(false);
  const [serviceNowLogsError, setServiceNowLogsError] = useState<string | null>(null);
  /** Controlled edit mode for Access Request tickets — syncs triage card with detail section */
  const [isAccessRequestEditing, setIsAccessRequestEditing] = useState(false);
  /** Lifted modification notes from AccessRequestTriageCard — shared with AccessRequestDetailSection */
  const [arModifyNotes, setArModifyNotes] = useState("");
  /** Lifted edited fields from AccessRequestDetailSection — used to build Modify payload */
  const [arEditedFields, setArEditedFields] = useState<{
    accessType?: string;
    organization?: string;
    department?: string;
    role?: string;
    roleId?: number;
    selectedLocationIds?: string[];
    selectedLocations?: any[];
  }>({});
  /** Loading/submitting state for AR triage actions */
  const [isArTriageSubmitting, setIsArTriageSubmitting] = useState(false);
  /** Capture approval failure error for RCA flow integration */
  const [arTriageFailureError, setArTriageFailureError] = useState<string>("");

  const normalizeRoleName = (value: unknown): string =>
    String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

  const TRIAGE_ROLE_ID_FALLBACK: Record<string, number> = {
    superuser: 1,
    super_user: 1,
    superuserrole: 1,
    coe: 2,
    regiondivisionleader: 3,
    regionleaderdivision: 3,
    marketleader: 7,
    siteleader: 4,
    sitesupervisor: 5,
    checker: 6,
    certifier: 7,
    inventorycoordinator: 8,
    teamlead: 9,
    developer: 15,
    // Delivery department roles
    deliverydriver: 11,
    deliverydistrictleader: 12,
    areadeliverymanager: 13,
    deliverymarketdirector: 14,
    deliverymarketleader: 14,
    deliveryseniorleadership: 16,
    deliverycoe: 17,
  };

  const inferScopedLocationType = (roleName: string): "LOCATION" | "MARKET" | "REGION" => {
    const normalizedRole = normalizeRoleName(roleName);
    if (normalizedRole.includes("market")) {
      return "MARKET";
    }
    if (normalizedRole.includes("region") || normalizedRole.includes("division")) {
      return "REGION";
    }
    return "LOCATION";
  };

  const resolveRoleIdForTriage = async (department: string, roleName: string): Promise<number | undefined> => {
    if (!roleName.trim()) {
      return undefined;
    }

    try {
      const api = createApiClient();
      const params = department.trim() ? { department: department.trim() } : undefined;
      const response = await api.get("/admin/users/roles", params ? { params } : undefined);

      const payload = response?.data;
      const rawRoles = Array.isArray(payload)
        ? payload
        : payload?.content ?? payload?.data ?? payload?.roles ?? [];

      if (!Array.isArray(rawRoles)) {
        return undefined;
      }

      const target = normalizeRoleName(roleName);
      const matchingRole = rawRoles.find((role: any) => {
        const candidateName = role?.name ?? role?.roleName ?? role?.ROLE_NAME ?? "";
        return normalizeRoleName(candidateName) === target;
      });

      const rawRoleId = matchingRole?.id ?? matchingRole?.roleId ?? matchingRole?.ROLE_ID;
      const numericRoleId = Number(rawRoleId);
      if (Number.isFinite(numericRoleId)) {
        return numericRoleId;
      }
    } catch {
      // Fall through to static fallback map.
    }

    const normalized = normalizeRoleName(roleName);
    return TRIAGE_ROLE_ID_FALLBACK[normalized];
  };

  /** Build and submit an AR triage API call */
  const handleArTriage = async (action: "approve" | "modify" | "deny", opts?: { modifyNotes?: string; denialReason?: string }) => {
    const backendId = ticket.backendTicketId;
    if (!backendId) {
      toast.show("Cannot submit: ticket ID not available.", "error");
      return;
    }
    setIsArTriageSubmitting(true);
    try {
      let payload: any = { action };
      if (action === "approve") {
        // Resolve the roleId from the live API (same as the modify path) so that
        // Delivery department roles — whose IDs were not stored on the ticket when
        // the ticket was originally submitted — are correctly provisioned on approval.
        const approveDepartment = ticket.accessRequest?.department ?? "";
        const approveRole = ticket.accessRequest?.role ?? "";
        if (approveDepartment && approveRole) {
          const approveRoleId = await resolveRoleIdForTriage(approveDepartment, approveRole);
          if (approveRoleId != null) {
            payload.modifiedFields = { roleId: approveRoleId };
          }
        }
      }
      if (action === "modify") {
        payload.modificationNotes = opts?.modifyNotes ?? arModifyNotes;
        // Build modifiedFields from the lifted edit state
        const modifiedFields: Record<string, any> = {};
        if (arEditedFields.accessType) modifiedFields.accessType = arEditedFields.accessType;
        if (arEditedFields.organization) modifiedFields.organization = arEditedFields.organization;
        if (arEditedFields.department) modifiedFields.department = arEditedFields.department;
        if (arEditedFields.role) modifiedFields.role = arEditedFields.role;

        const effectiveDepartment = arEditedFields.department ?? ticket.accessRequest?.department ?? "";
        const effectiveRole = arEditedFields.role ?? ticket.accessRequest?.role ?? "";
        const resolvedRoleId = arEditedFields.roleId
          ?? await resolveRoleIdForTriage(effectiveDepartment, effectiveRole);
        if (resolvedRoleId != null) {
          modifiedFields.roleId = resolvedRoleId;
        }

        // Prefer the full location objects built by AccessRequestDetailSection
        // (arEditedFields.selectedLocations) — these already carry the correct
        // database numeric locationId resolved from /admin/locations via
        // editLocationIdMap. Re-deriving from ticket.accessRequest.locations
        // (the OLD ticket data) was the source of the locationId=name bug,
        // since that old data itself may have stored the site name as the id.
        if (arEditedFields.selectedLocations?.length) {
          modifiedFields.locations = arEditedFields.selectedLocations.map((loc: any) => {
            const locationType = String(loc.locationType ?? "LOCATION").toUpperCase();
            return {
              locationType,
              locationId: String(loc.locationId ?? loc.site ?? loc.locationName ?? ""),
              locationName: String(loc.locationName ?? loc.site ?? loc.locationId ?? ""),
              ...(locationType === "MARKET" ? { region: loc.region ?? null } : {}),
              ...(locationType === "LOCATION"
                ? { region: loc.region ?? null, market: loc.market ?? null }
                : {}),
            };
          });
        } else if (arEditedFields.selectedLocationIds?.length) {
          // Fallback for callers that only lifted selectedLocationIds (no full
          // location objects available) — match against ticket's existing locations.
          const allLocs: any[] = ticket.accessRequest?.locations ?? [];
          modifiedFields.locations = arEditedFields.selectedLocationIds
            .map((id) => {
              const matched = allLocs.find((l: any) =>
                String(l.locationId ?? "") === id ||
                String(l.locationName ?? "") === id ||
                String(l.market ?? "") === id ||
                String(l.region ?? "") === id
              );

              if (matched) {
                const matchedType = String(matched.locationType ?? "LOCATION").toUpperCase();
                return {
                  locationType: matchedType,
                  locationId: String(matched.locationId ?? id),
                  locationName: String(
                    matched.locationName
                    ?? (matchedType === "MARKET" ? matched.market : matchedType === "REGION" ? matched.region : id)
                    ?? id
                  ),
                  ...(matchedType === "MARKET"
                    ? {
                        region: String(matched.region ?? "").trim() || null,
                      }
                    : {}),
                  ...(matchedType === "LOCATION"
                    ? {
                        region: String(matched.region ?? "").trim() || null,
                        market: String(matched.market ?? "").trim() || null,
                      }
                    : {}),
                };
              }

              const inferredType = inferScopedLocationType(effectiveRole);

              return {
                locationType: inferredType,
                locationId: String(id),
                locationName: String(id),
                ...(inferredType === "MARKET" ? { region: null } : {}),
              };
            })
            .filter(Boolean);
        }
        if (Object.keys(modifiedFields).length > 0) payload.modifiedFields = modifiedFields;
      }
      if (action === "deny") {
        payload.denialReason = opts?.denialReason ?? "";
      }
      const result = await dispatch(triageAccessRequest(backendId, payload) as any);
      if (result?.success) {
        toast.show(
          action === "approve" ? "Access request approved & provisioned." :
          action === "modify" ? "Access request modified & approved." :
          "Access request denied.",
          "success",
        );
        setIsAccessRequestEditing(false);
        setArModifyNotes("");
        setArEditedFields({});
      } else {
        // On approval failure, capture error and open RCA modal
        const errorMsg = result?.message ?? "Approval action failed. Please try again.";
        setArTriageFailureError(errorMsg);
        setShowCloseTicketModal(true);
      }
    } finally {
      setIsArTriageSubmitting(false);
    }
  };

  /**
   * Adapter callback for RCA submission when approval failed.
   * Enriches RCA notes with the original approval error context and calls handleCloseTicket.
   */
  const handleArTriageFailureRca = async (
    catId: number,
    subCatId: number,
    rcaCategory: string,
    rcaNotes: string,
    resolvedBy: string
  ) => {
    // Prepend approval error context to RCA notes
    const enrichedNotes = `[APPROVAL ERROR - AUTO-TRIGGERED RCA]\nOriginal Error: ${arTriageFailureError}\n\nRoot Cause Analysis:\n${rcaNotes}`;

    // Call existing close ticket handler with enriched RCA notes
    const result = await handleCloseTicket(catId, subCatId, rcaCategory, enrichedNotes, resolvedBy);

    // Clear error state on successful RCA submission
    if (result?.type === "success") {
      setArTriageFailureError("");
    }

    return result;
  };

  /** Resolve by IT */
  const handleArResolveByIT = async () => {
    const backendId = ticket.backendTicketId;
    if (!backendId) {
      toast.show("Cannot submit: ticket ID not available.", "error");
      return;
    }
    setIsArTriageSubmitting(true);
    try {
      const result = await dispatch(resolveAccessRequestByIT(backendId) as any);
      if (result?.success) {
        toast.show("Ticket resolved by IT and closed.", "success");
      } else {
        toast.show(result?.message ?? "Resolve by IT failed. Please try again.", "error");
      }
    } finally {
      setIsArTriageSubmitting(false);
    }
  };
  // Activity image upload state
  const [activityImages, setActivityImages] = useState<string[]>([]);
  const [activityImageFileNames, setActivityImageFileNames] = useState<string[]>([]);
  const [activityImageUrls, setActivityImageUrls] = useState<string[]>([]);
  const [activityImagesUploading, setActivityImagesUploading] = useState(false);

  /* ── Refs (must be at top level, not inside if) ── */
  const scrollViewRef = useRef<ScrollView>(null);
  const tabContentY = useRef(0);
  const sectionOffsets = useRef<Record<MobileTab, number>>({
    Details: 0, Impact: 0, Notes: 0, Activity: 0,
  });
  const statusTriggerRef = useRef<View>(null);
  const urgencyTriggerRef = useRef<View>(null);
  const assignedTriggerRef = useRef<View>(null);
  const assignedTeamTriggerRef = useRef<View>(null);
  const affectedLocationsRef = useRef<View>(null);
  const attemptedAbsorbedFetchesRef = useRef<Set<string>>(new Set());

  /* ── Helpers ── */
  const openDrop = (type: "status" | "urgency" | "assigned" | "team", triggerRef: React.RefObject<View>) => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      setDropPos({ x, y: y + height + 4, width });
      setOpenDropdown(type);
    });
  };

  const scrollToSection = (tab: MobileTab) => {
    setActiveTab(tab);
    scrollViewRef.current?.scrollTo({
      y: tabContentY.current + sectionOffsets.current[tab],
      animated: true,
    });
  };

  const {
    ticket, savedInfo, isEditingInfo, editDraft, commentText, noteText,
    editErrors,
    notes, activity, locationsToShow, impactedPickersCount, hasMoreLocations, showAllLocations,
    resolutionPath, assignedTo, incidentNumber, isWidespread, isMergeModalOpen, isAdoModalOpen,
    isTriageSending, hasTriageChanges,
    startEdit, cancelEdit, saveEdit, patchDraft, postComment, postCommentFull, saveNote, saveNoteFull,
    setCommentText, setNoteText, setShowAllLocations, changeStatus,
    setResolutionPath, setAssignedTo, setIncidentNumber, toggleWidespread, addImpactLocation,
    removeImpactLocation, addSecondaryStakeholder, handleMerge, closeMergeModal, confirmMergeTickets,
    handleCreateADO, closeAdoModal, confirmCreateAdoWorkItem,
    handleManageImpact, handleResolve, handleCloseTicket, handleSendTriageUpdate, changeUrgency,
    assignedTeams, assignedTeamName, isAssignedTeamInactive, changeAssignedTeam,
  } = useTicketDetail(ticketId);
  const { isSustainTeamMember, isSuperUser } = useTicketingAccess();

  /** Wrapper that handles the async changeStatus result and shows toast feedback */
  const handleStatusChange = async (status: TicketStatus) => {
    if (status === "resolved") {
      setShowCloseTicketModal(true);
      return;
    }
    const result = await changeStatus(status);
    if (result) {
      toast.show(result.msg, result.type);
    }
  };

  const currentStatus = normalizeTicketStatusKey(ticket.status);
  const urgencyValue = savedInfo.urgency || ticket.urgency;
  const currentUrgency = urgencyValue ? normalizeUrgencyKey(urgencyValue) : "";

  /* ── Absorbed ticket summaries for MergedTicketsSection ── */
  const ticketDetails = useSelector((s: RootState) => s.ticketingPortal.ticketDetails);
  const sustainQueue = useSelector((s: RootState) => s.ticketingPortal.sustainQueue);
  const myTickets = useSelector((s: RootState) => s.ticketingPortal.myTickets);
  const ticketDetailSource = useSelector((s: RootState) => s.ticketingPortal.ticketDetailSource);
  const sustainTeamMembers = useSelector((s: RootState) => s.ticketingPortal.sustainTeamMembers);
  const selectedTicketIdFromStore = useSelector((s: RootState) => s.ticketingPortal.selectedTicketId);
  /** True while the ticket detail is loading from the API */
  const isTicketDetailLoading = useSelector((s: RootState) => s.ticketingPortal.detailLoading);
  /** When set, indicates that the currently-loading ticket is an Access Request. */
  const pendingAccessRequestTicketId = useSelector((s: RootState) => s.ticketingPortal.pendingAccessRequestTicketId);
  // Basic-access (no Navi) user viewing their own ticket — forces read-only.
  const noAccessMode = useSelector((s: RootState) => (s.appData as any)?.noAccessMode === true);
  /** Effective ticket id used to look up queue rows before detail has loaded */
  const effectiveTicketId = ticketId || selectedTicketIdFromStore;
  /**
   * True when the ticket is (or will be) an Access Request.
   * During loading, FALLBACK_TICKET has category "System Issue", so we also
   * probe sustainQueue / myTickets to detect AR before the detail API returns.
   * Additionally, check pendingAccessRequestTicketId to detect newly submitted AR tickets.
   */
  // Determine whether this ticket is an Access Request. The selected ticket
  // id can be either a numeric id or a ticket number string (from Submit flow),
  // so check both `id` and `ticketNumber` properties on queue rows.
  const isAccessRequestTicket =
    ticket.category === "Access Request" ||
    !!pendingAccessRequestTicketId ||
    sustainQueue.some((r) =>
      (String(r.id) === String(effectiveTicketId) || String(r.ticketNumber) === String(effectiveTicketId)) && r.category === "Access Request"
    ) ||
    myTickets.some((r) =>
      (String(r.id) === String(effectiveTicketId) || String(r.ticketNumber) === String(effectiveTicketId)) && r.category === "Access Request"
    );
  const detailError = useSelector((s: RootState) => s.ticketingPortal.error);
  const absorbedTickets: AbsorbedTicketSummary[] = (ticket.absorbedTicketIds ?? []).map((id) => {
    const detail = ticketDetails[id];
    const queueRow = sustainQueue.find((r) => r.id === id);
    return {
      id,
      title: detail?.title ?? queueRow?.title ?? id,
      submitted: detail?.submitted ?? queueRow?.submitted ?? "",
      submittedBy: detail?.submittedBy ?? queueRow?.submittedBy,
    };
  });

  /* ── Sustain team member name options for Assigned-To dropdown ── */
  const memberNameOptions: string[] = ["Unassigned", ...sustainTeamMembers.map((m) => formatUserName(m.userEmail))];

  const categoryConfig = useSelector((s: RootState) => s.ticketingPortal.submitTicketCategoryConfig);
  const dynamicFieldsEmpty = useSelector((s: RootState) => s.ticketingPortal.submitTicketDynamicFields.length === 0);
  const currentUserEmail = useSelector((s: RootState) => s.appData?.userInfo?.email ?? "");

  const categoryConfigStale = useSelector(isCategoryConfigStale);

  /* ── Fetch form config (categories + dynamic fields) if either is missing or stale for this location/role ── */
  useEffect(() => {
    if (categoryConfigStale || dynamicFieldsEmpty) {
      dispatch(fetchSubmitTicketFormConfigAndFields() as any);
    }
  }, [categoryConfigStale, dynamicFieldsEmpty, dispatch]);

  /* ── Fetch sustain team members once ── */
  useEffect(() => {
    if (sustainTeamMembers.length === 0) {
      dispatch(fetchSustainTeamMembers(""));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Fetch missing absorbed tickets ── */
  useEffect(() => {
    attemptedAbsorbedFetchesRef.current.clear();
  }, [ticket.id]);

  useEffect(() => {
    const missingIds = (ticket.absorbedTicketIds ?? []).filter(
      (id) =>
        !ticketDetails[id] &&
        !sustainQueue.find((r) => r.id === id) &&
        !attemptedAbsorbedFetchesRef.current.has(id)
    );
    
    if (missingIds.length > 0) {
      console.log("[TicketDetailScreen] Fetching missing absorbed tickets:", missingIds);
      missingIds.forEach((id) => {
        attemptedAbsorbedFetchesRef.current.add(id);
        dispatch(fetchTicketDetail(id, { setAsSelected: false, silent: true }) as any);
      });
    }
  }, [ticket.absorbedTicketIds, ticketDetails, sustainQueue, dispatch]);

  /* ── Re-Open logic ── */
  const canReopen = (() => {
    if (!TICKETING_REOPEN_ELIGIBLE_STATUSES.includes(ticket.status)) return false;
    const closeReference = ticket.closedAt || ticket.lastUpdated;
    const closedMs = new Date(closeReference).getTime();
    if (Number.isNaN(closedMs)) return false;
    const windowMs = TICKETING_REOPEN_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - closedMs <= windowMs;
  })();

  const { enabled: serviceNowEnabled } = useServiceNowEnabled();
  // Backend stores merged tickets as status="Closed" (maps to "resolved") with a non-null parentTicketRef.
  // parentTicketRef is the source of truth for detecting an absorbed/merged ticket and for navigation.
  const isMergedClosed = ticket.status === "resolved" && !!ticket.parentTicketRef;
  const isClosed = ticket.status === "resolved";
  const normalizedAssignedTo = (assignedTo || "").trim().toLowerCase();
  const normalizedCurrentUserEmail = currentUserEmail.trim().toLowerCase();
  const currentUserDisplayName = formatUserName(currentUserEmail).trim().toLowerCase();
  const isCurrentUserAssignee = Boolean(
    normalizedAssignedTo &&
      (
        normalizedAssignedTo === normalizedCurrentUserEmail ||
        normalizedAssignedTo === currentUserDisplayName ||
        normalizedAssignedTo.startsWith(`${currentUserDisplayName} (`)
      )
  );
  /** TKT_Teams.id the logged-in Sustain Team member belongs to (null if unknown/not on the roster). */
  const myTeamId = useMemo(() => {
    const email = normalizedCurrentUserEmail;
    if (!email) return null;
    const me = sustainTeamMembers.find((m) => m.userEmail.toLowerCase() === email);
    return me?.teamId ?? null;
  }, [sustainTeamMembers, normalizedCurrentUserEmail]);
  // Unassigned tickets remain fully editable; once a team is assigned, only members
  // of that team may edit — other Sustain Team members get a read-only view.
  const isOwnTeamOrUnassigned = ticket.assignedTeamId == null || (myTeamId != null && ticket.assignedTeamId === myTeamId);
  const canViewTriage = isSustainTeamMember;
  const canEditTriage = isSustainTeamMember && !isClosed && !noAccessMode && isOwnTeamOrUnassigned;
  const canViewInternalNotes = isSustainTeamMember;
  const canEditNotes = !isClosed && !noAccessMode && isOwnTeamOrUnassigned;
  const canEditSubmittedDetails = isSustainTeamMember && !isClosed && isCurrentUserAssignee && !noAccessMode && isOwnTeamOrUnassigned;
  const shouldOpenServiceNowModal =
    serviceNowEnabled &&
    canEditTriage &&
    resolutionPath === "Requires IT" &&
    !(ticket.incidentId ?? "").trim();
  const mobileTabs = canViewInternalNotes
    ? MOBILE_TABS
    : MOBILE_TABS.filter((tab) => tab !== "Notes");

  useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const shouldPollServiceNowStatus =
      serviceNowEnabled &&
      canViewTriage &&
      !!ticket.backendTicketId &&
      resolutionPath === "Requires IT" &&
      !(ticket.incidentId ?? "").trim();

    const loadServiceNowStatus = async () => {
      if (!shouldPollServiceNowStatus) {
        if (isMounted) {
          setServiceNowStatus(null);
        }
        return;
      }

      try {
        const api = createApiClient();
        const response = await api.get<ServiceNowIncidentStatus>(
          `/servicenow/status?ticketId=${encodeURIComponent(String(ticket.backendTicketId))}`
        );

        if (isMounted) {
          setServiceNowStatus(response.data);
        }
      } catch (error) {
        if (isMounted) {
          setServiceNowStatus(null);
        }
      }
    };

    loadServiceNowStatus();

    if (shouldPollServiceNowStatus) {
      intervalId = setInterval(loadServiceNowStatus, 10000);
    }

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [canViewTriage, resolutionPath, ticket.backendTicketId, ticket.incidentId]);

  useEffect(() => {
    let isMounted = true;

    const loadServiceNowLogs = async () => {
      if (!serviceNowEnabled || !canViewTriage || !ticket.id) {
        if (isMounted) {
          setServiceNowLogs([]);
          setServiceNowLogsError(null);
        }
        return;
      }

      setServiceNowLogsLoading(true);
      setServiceNowLogsError(null);

      try {
        const api = createApiClient();
        const response = await api.get<ServiceNowLogEntry[]>(
          `/servicenow/logs/${encodeURIComponent(ticket.id)}`
        );

        if (isMounted) {
          setServiceNowLogs(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error: any) {
        if (isMounted) {
          setServiceNowLogs([]);
          setServiceNowLogsError(
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load ServiceNow logs for this ticket."
          );
        }
      } finally {
        if (isMounted) {
          setServiceNowLogsLoading(false);
        }
      }
    };

    loadServiceNowLogs();

    return () => {
      isMounted = false;
    };
  }, [canViewTriage, ticket.id]);

  const shouldShowServiceNowErrorBanner =
    serviceNowEnabled &&
    !!serviceNowStatus && ["FAILED", "RETRYING", "EXHAUSTED"].includes(serviceNowStatus.status);

  const isTicketNotFound = detailError === "Ticket not found. Please verify the Ticket ID.";
  const isActiveWidespreadTicket = Boolean(ticket.isWidespread) && ticket.status !== "resolved";

  /* ── Access control: active widespread incidents are viewable by all users; otherwise standard role checks apply ── */
  const {
    hasAccess: hasTicketAccess,
    isTicketSubmitter,
    isSecondaryStakeholder,
  } = useTicketViewAccess(ticket.submittedBy, ticket.stakeholders, isActiveWidespreadTicket, isDeepLinked);

  /**
   * Derives the actorRoleTag to send to POST /ticketing/activities.
   * Priority: Sustain Team (own/unassigned team only) > Submitter > Stakeholder > Super User
   */
  const actorRoleTag = isSustainTeamMember && isOwnTeamOrUnassigned
    ? "Sustain Team"
    : isTicketSubmitter
    ? "Submitter"
    : isSecondaryStakeholder
    ? "Stakeholder"
    : isSuperUser
    ? "Super User"
    : null; // null = no comment permission

  /** Sustain Team, ticket submitter, stakeholders, and Super Users may all post comments */
  const canPostComment = actorRoleTag !== null && !isClosed && !noAccessMode;

  const handleTriageSend = async (): Promise<{ msg: string; type: "success" | "info" | "warning" | "error" }> => {
    // Validate Assigned To is not empty for Requires IT
    if (resolutionPath === "Requires IT" && (!assignedTo || assignedTo === "Unassigned")) {
      return {
        msg: "Please assign the ticket to a team member before routing to IT.",
        type: "error",
      };
    }

    if (shouldOpenServiceNowModal) {
      if (!ticket.backendTicketId) {
        return {
          msg: "Ticket details are still loading. Please wait a moment and try again.",
          type: "error",
        };
      }

      setShowServiceNowModal(true);
      return {
        msg: "Review the ServiceNow incident draft before proceeding.",
        type: "info",
      };
    }

    return handleSendTriageUpdate();
  };

  const handleServiceNowProceed = async (draft: ServiceNowIncidentDraft) => {
    if (!ticket.backendTicketId) {
      toast.show("Ticket details are still loading. Please wait a moment and try again.", "error");
      return;
    }

    setIsServiceNowSubmitting(true);
    try {
      const api = createApiClient();
      const existingIncidentNumber = draft.existingIncidentNumber.trim();
      const endpoint = existingIncidentNumber
        ? `/servicenow/attach-incident?ticketId=${encodeURIComponent(String(ticket.backendTicketId))}`
        : `/servicenow/create-incident?ticketId=${encodeURIComponent(String(ticket.backendTicketId))}`;

      const payload = {
        shortDescription: draft.shortDescription,
        description: draft.description,
        urgency: draft.urgency,
        location: draft.location,
        sapId: draft.sapId,
        submitterEmail: draft.submitterEmail,
        existingIncidentNumber,
      };

      const response = await api.post(endpoint, payload);
      const incidentNumberFromResponse =
        response?.data?.incidentNumber || existingIncidentNumber || draft.ntiId;

      const triageResult = await handleSendTriageUpdate(incidentNumberFromResponse);
      if (triageResult.type === "error") {
        toast.show(triageResult.msg, triageResult.type);
        return;
      }

      setShowServiceNowModal(false);
      toast.show(
        response?.data?.message || `ServiceNow Incident [${incidentNumberFromResponse}] created and linked successfully.`,
        "success"
      );
      await dispatch(fetchTicketDetail(ticket.id) as any);
    } catch (error: any) {
      toast.show(
        error?.response?.data?.message || error?.message || "Failed to submit the ServiceNow incident draft.",
        "error"
      );
    } finally {
      setIsServiceNowSubmitting(false);
    }
  };

  const handleServiceNowRetryNow = async () => {
    if (!ticket.backendTicketId) {
      toast.show("Ticket details are still loading. Please wait a moment and try again.", "error");
      return;
    }

    setIsServiceNowRetrying(true);
    try {
      const api = createApiClient();
      const response = await api.post(
        `/servicenow/retry-now?ticketId=${encodeURIComponent(String(ticket.backendTicketId))}`
      );

      toast.show(response?.data?.message || "Retry initiated. System will attempt creation now.", "success");

      const statusResponse = await api.get<ServiceNowIncidentStatus>(
        `/servicenow/status?ticketId=${encodeURIComponent(String(ticket.backendTicketId))}`
      );
      setServiceNowStatus(statusResponse.data);
    } catch (error: any) {
      toast.show(
        error?.response?.data?.message || error?.message || "Failed to trigger ServiceNow retry.",
        "error"
      );
    } finally {
      setIsServiceNowRetrying(false);
    }
  };

  const handleConfirmReopen = () => {
    dispatch(
      prePopulateSubmitTicket({
        title: ticket.title,
        description: ticket.description,
        stakeholders: ticket.stakeholders ?? "",
        category: ticket.category,
        subCategory: ticket.subcategory ?? "",
        categoryId: ticket.categoryId,
        subCategoryId: ticket.subCategoryId,
        dynamicValues: (ticket.dynamicValues ?? [])
          .filter((v) => v.value != null && String(v.value).trim().length > 0)
          .map((v) => ({ fieldConfigId: v.fieldConfigId, value: v.value })),
        level: (ticket.ticketLevel as TicketLevel) ?? "Location",
        levelReference: ticket.market ?? "",
        urgency: toTitleCaseWord(normalizeUrgencyKey(savedInfo.urgency || ticket.urgency)) as UrgencyLevel,
        impactedPeople: ticket.peopleImpacted ?? "",
        loadsImpacted: (ticket.loadsImpacted ?? "") as LoadImpact,
        reopenedFromId: ticket.id,
      })
    );
    setShowReopenConfirm(false);
    onNavigate("Submit Ticket");
  };

  /** Handle removing an image from the activity images list */
  const handleRemoveActivityImage = (index: number) => {
    setActivityImages((prev) => prev.filter((_, i) => i !== index));
    setActivityImageFileNames((prev) => prev.filter((_, i) => i !== index));
    setActivityImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  /** Handle posting an activity/comment with images */
  const handlePostActivityWithImages = async () => {
    if (!commentText.trim() && activityImages.length === 0) {
      return;
    }

    if (!canPostComment) {
      toast.show("You do not have permission to post comments on this ticket.", "warning");
      return;
    }

    if (activityImagesUploading) {
      toast.show("Please wait for images to finish uploading.", "warning");
      return;
    }

    try {
      const success = await postCommentFull(commentText, activityImageFileNames, actorRoleTag ?? undefined);
      if (success) {
        setCommentText("");
        setActivityImages([]);
        setActivityImageFileNames([]);
        setActivityImageUrls([]);
        toast.show("Comment posted · syncing to ServiceNow", "success");
      } else {
        toast.show("Failed to post comment", "error");
      }
    } catch (error: any) {
      toast.show(error?.message || "Failed to post comment", "error");
    }
  };

  /** Handle picking files for activity/comment (mobile layout) */
  const handlePickActivityImage = async () => {
    // NOTE: Mobile activity section is handled by the ActivityStream component on web/iPad.
    // This handler is only called from the raw mobile layout. It uses uploadTicketFile.
    // Inline file upload is not available on the raw mobile section — the mobile section
    // delegates to the ActivityStream component which has full file-upload support.
    // We keep this stub to avoid removing associated UI; it shows a toast to guide users.
    toast.show("Use the Activity Stream below to attach files.", "info");
  };

  const handleOpenOriginalTicket = (originalTicketId: string) => {
    dispatch(setSelectedTicketId(originalTicketId));
    onNavigate("Ticket Detail");
  };

  const handleSubmitNewTicketFromClosed = () => {
    dispatch(
      prePopulateSubmitTicket({
        title: "",
        description: "",
        stakeholders: "",
        category: ticket.category,
        subCategory: ticket.subcategory ?? "",
        categoryId: ticket.categoryId,
        subCategoryId: ticket.subCategoryId,
        dynamicValues: [],
        level: (ticket.ticketLevel as TicketLevel) ?? "Location",
        levelReference: "",
        urgency: "Medium" as UrgencyLevel,
        impactedPeople: "",
        loadsImpacted: "" as LoadImpact,
        reopenedFromId: "",
      })
    );
    onNavigate("Submit Ticket");
  };

  /** Confirmation modal — shared between web and mobile layouts */
  const reopenConfirmModal = (
    <Modal
      transparent
      visible={showReopenConfirm}
      onRequestClose={() => setShowReopenConfirm(false)}
      animationType="fade"
      statusBarTranslucent
    >
      <View style={reo.backdrop}>
        <View style={reo.dialog}>
          <View style={reo.dialogHeader}>
            <MaterialIcons name="refresh" size={24} color="#16a34a" />
            <Text style={reo.dialogTitle}>Re-Open Ticket?</Text>
          </View>
          <Text style={reo.dialogBody}>
            Are you sure you want to re-open this ticket? If a different problem is occurring, please submit a new ticket instead.
          </Text>
          <View style={reo.dialogActions}>
            <Pressable
              style={({ pressed }) => [reo.btnCancel, pressed && { opacity: 0.75 }]}
              onPress={() => setShowReopenConfirm(false)}
            >
              <Text style={reo.btnCancelText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={({ pressed }) => [reo.btnReopen, pressed && { opacity: 0.75 }]}
              onPress={handleConfirmReopen}
            >
              <MaterialIcons name="refresh" size={14} color="#ffffff" />
              <Text style={reo.btnReopenText}>Re-Open</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );

  /** Assigned Team reassignment confirmation modal — mobile layout only (web uses TicketHero's own modal). */
  const teamReassignConfirmModal = (
    <Modal
      transparent
      visible={!!pendingTeam}
      onRequestClose={() => setPendingTeam(null)}
      animationType="fade"
      statusBarTranslucent
    >
      <View style={reo.backdrop}>
        <View style={reo.dialog}>
          <View style={reo.dialogHeader}>
            <MaterialIcons name="groups" size={22} color="#034895" />
            <Text style={reo.dialogTitle}>Change Team Assignment?</Text>
          </View>
          <Text style={reo.dialogBody}>
            You are changing this team assignment, are you sure you wish to move this to the {pendingTeam?.name} Team?
          </Text>
          <View style={reo.dialogActions}>
            <Pressable
              style={({ pressed }) => [reo.btnCancel, pressed && { opacity: 0.75 }]}
              onPress={() => setPendingTeam(null)}
            >
              <Text style={reo.btnCancelText}>No</Text>
            </Pressable>
            <Pressable
              style={({ pressed }) => [reo.btnReopen, pressed && { opacity: 0.75 }]}
              onPress={async () => {
                const team = pendingTeam;
                setPendingTeam(null);
                if (team) {
                  const r = await changeAssignedTeam(team.id);
                  toast.show(r.msg, r.type);
                }
              }}
            >
              <Text style={reo.btnReopenText}>Yes</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );

  const actionModals = (() => {
    const dvList = ticket.dynamicValues ?? [];
    const getDvField = (pattern: RegExp): string =>
      dvList.find((dv) => pattern.test((dv.historicalLabel ?? "").toLowerCase()))?.value ?? "";

    // Navigator module
    const navigatorModule = getDvField(/navigator\s*module/);

    // Vision Pick (WES) / Bug Review fields extracted from ticket dynamic values
    const ticketVersionField   = getDvField(/\bversion\b/);
    const ticketSiteField      = getDvField(/\bsite\b/);
    const ticketWesEquipField  = getDvField(/wes\s*equipment|wes\s*equip/);
    const ticketDateTimeField  = getDvField(/date[\s\/]?time|datetime|timestamp/);
    const ticketPrecondField   = getDvField(/precondition/);
    const ticketStepsField     = getDvField(/steps?\s*to\s*reproduce|repro/);
    const ticketExpectedField  = getDvField(/expected\s*result/);
    const ticketCurrentField   = getDvField(/current\s*result|actual\s*result/);

    return (
      <TicketDetailActionModals
        mergeOpen={isMergeModalOpen} adoOpen={isAdoModalOpen}
        primaryTicketId={ticket.id} primaryTicketTitle={ticket.title}
        adoCategory={ticket.category} adoSubcategory={ticket.subcategory}
        adoDescription={ticket.description}
        submitterEmail={ticket.submittedBy}
        assignedToName={assignedTo}
        ticketPriority={ticket.urgency}
        dashboardLink={`${process.env.REACT_APP_BASE_URL || ''}/tickets/${ticket.id}`}
        ticketModule={navigatorModule}
        ticketIssueDescription={ticket.description}
        ticketVersion={ticketVersionField}
        ticketSite={ticketSiteField}
        ticketWesEquipment={ticketWesEquipField}
        ticketDateTimeStamp={ticketDateTimeField}
        ticketPreconditions={ticketPrecondField}
        ticketStepsToReproduce={ticketStepsField}
        ticketExpectedResults={ticketExpectedField}
        ticketCurrentResults={ticketCurrentField}
        onCloseMerge={closeMergeModal}
        onConfirmMerge={async (ids) => {
          const r = await confirmMergeTickets(ids);
          toast.show(r.msg, r.type);
        }}
        onFlagWidespread={canEditTriage ? () => {
          const r = toggleWidespread();
          if (r) toast.show(r.msg, r.type);
        } : undefined}
        onCloseAdo={closeAdoModal}
        onConfirmAdo={async (payload) => {
          const r = await confirmCreateAdoWorkItem(payload);
          // Only show toast on success; errors are handled by the modal's error banner
          if (r.type !== "error") {
            toast.show(r.msg, r.type);
          }
          return r;
        }}
      />
    );
  })();

  if (isTicketNotFound) {
    return (
      <View style={styles.screenRoot}>
        <View style={[styles.container, { justifyContent: "center", alignItems: "center", paddingHorizontal: 24 }]}> 
          <View style={{ width: "100%", maxWidth: 640, backgroundColor: "#ffffff", borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 12, padding: 20 }}>
            <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 10 }}>
              <MaterialIcons name="error-outline" size={20} color="#b45309" />
              <Text style={{ marginLeft: 8, fontSize: 18, fontWeight: "700", color: "#111827" }}>Ticket not found</Text>
            </View>
            <Text style={{ fontSize: 14, lineHeight: 22, color: "#374151", marginBottom: 16 }}>
              Ticket not found. Please verify the Ticket ID.
            </Text>
            <Pressable
              style={({ pressed }) => [{ alignSelf: "flex-start", flexDirection: "row", alignItems: "center", backgroundColor: "#034895", borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10 }, pressed && { opacity: 0.85 }]}
              onPress={() => onNavigate(ticketDetailSource === "Homepage Banner" ? "Ticketing Portal" : (ticketDetailSource || "My Tickets"))}
            >
              <MaterialIcons name="chevron-left" size={18} color="#ffffff" />
              <Text style={{ color: "#ffffff", fontSize: 14, fontWeight: "700" }}>Back to {ticketDetailSource === "Homepage Banner" ? "Ticketing Portal" : (ticketDetailSource || "My Tickets")}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    );
  }

  /* ── Access Denied ── */
  if (!hasTicketAccess) {
    return (
      <TicketAccessDenied
        ticketDetailSource={ticketDetailSource}
        onNavigateBack={() =>
          onNavigate(
            ticketDetailSource === "Homepage Banner"
              ? "Ticketing Portal"
              : ticketDetailSource || "My Tickets"
          )
        }
      />
    );
  }

  /* ── iPhone Mobile Layout ────────────────────────────────────────────────── */
  if (isiPhone) {
    const locDisplay = showAllLocs ? ticket.affectedLocations : ticket.affectedLocations.slice(0, 5);
    const moreCount = ticket.affectedLocations.length - 5;

    return (
      <View style={styles.screenRoot}>
        {/* ── Dropdown Overlay Modal (renders above everything) ── */}
        <Modal
          transparent
          visible={openDropdown !== null}
          onRequestClose={() => setOpenDropdown(null)}
          animationType="none"
          statusBarTranslucent
        >
          <Pressable style={StyleSheet.absoluteFillObject} onPress={() => setOpenDropdown(null)} />
          <View style={[md.overlayDropMenu, { top: dropPos.y, left: dropPos.x, width: dropPos.width }]}>
            {openDropdown === "status" && STATUS_OPTIONS.map((opt) => (
              <Pressable
                key={opt.value}
                style={[md.dropItem, opt.value === currentStatus && md.dropItemActive]}
                onPress={() => { if (canEditTriage) { handleStatusChange(opt.value); } setOpenDropdown(null); }}
              >
                <Text style={[md.dropItemText, opt.value === currentStatus && md.dropItemTextActive]}>{opt.label}</Text>
              </Pressable>
            ))}
            {openDropdown === "urgency" && URGENCY_OPTIONS.map((opt) => (
              <Pressable
                key={opt.value}
                style={[md.dropItem, opt.value === currentUrgency && md.dropItemActive]}
                onPress={() => { if (canEditTriage) { changeUrgency(opt.value); } setOpenDropdown(null); }}
              >
                <Text style={[md.dropItemText, opt.value === currentUrgency && md.dropItemTextActive]}>{opt.label}</Text>
              </Pressable>
            ))}
            {openDropdown === "assigned" && memberNameOptions
              .filter((opt) => {
                // Show "Unassigned" only when no assignment is made
                if (opt === "Unassigned") {
                  return !assignedTo || assignedTo === "Unassigned";
                }
                return true;
              })
              .map((opt) => (
              <Pressable
                key={opt}
                style={[md.dropItem, (assignedTo || "Unassigned") === opt && md.dropItemActive]}
                onPress={() => { if (canEditTriage) { setAssignedTo(opt); } setOpenDropdown(null); }}
              >
                <Text style={[md.dropItemText, (assignedTo || "Unassigned") === opt && md.dropItemTextActive]}>{opt}</Text>
              </Pressable>
            ))}
            {openDropdown === "team" && (
              <>
                {ticket.assignedTeamId == null ? (
                  <Pressable key="unassigned" style={[md.dropItem, md.dropItemActive]}>
                    <Text style={[md.dropItemText, md.dropItemTextActive]}>Unassigned</Text>
                  </Pressable>
                ) : null}
                {assignedTeams.map((team) => (
                  <Pressable
                    key={team.id}
                    style={[md.dropItem, team.id === ticket.assignedTeamId && md.dropItemActive]}
                    onPress={() => {
                      setOpenDropdown(null);
                      if (canEditTriage) { setPendingTeam({ id: team.id, name: team.name }); }
                    }}
                  >
                    <Text style={[md.dropItemText, team.id === ticket.assignedTeamId && md.dropItemTextActive]}>{team.name}</Text>
                  </Pressable>
                ))}
              </>
            )}
          </View>
        </Modal>

        <View style={md.root}>
          {/* Header */}
          <View style={md.header}>
            <Pressable onPress={() => onNavigate(ticketDetailSource === "Homepage Banner" ? "Ticketing Portal" : ticketDetailSource)} style={md.backBtn} hitSlop={8}>
              <MaterialIcons name="arrow-back" size={22} color="#1a2332" />
            </Pressable>
            <View style={{ flex: 1 }}>
              <Text style={md.headerTitle}>Ticket Details</Text>
              <Text style={md.headerSub}>Ticket Portal › {ticketDetailSource}</Text>
            </View>
            {canReopen ? (
              <Pressable
                style={({ pressed }) => [md.reopenBtn, pressed && { opacity: 0.75 }]}
                onPress={() => setShowReopenConfirm(true)}
              >
                <MaterialIcons name="refresh" size={14} color="#ffffff" />
                <Text style={md.reopenBtnText}>Re-Open</Text>
              </Pressable>
            ) : null}
          </View>

          <ScrollView
            ref={scrollViewRef}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 48 }}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="interactive"
            automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
          >
            {/* Closed warning banner */}
            {ticket.status === "resolved" ? (
              <View style={[mib.banner, { marginHorizontal: 16, marginTop: 12, backgroundColor: "#fef3c7", borderLeftColor: "#f59e0b", borderLeftWidth: 4, flexDirection: "row", alignItems: "flex-start", gap: 8 }]}>
                <MaterialIcons name="lock" size={18} color="#d97706" style={{ marginTop: 2 }} />
                <View style={{ flex: 1 }}>
                  <Text style={[mib.text, { color: "#92400e", fontWeight: "600", marginBottom: 4 }]}>
                    This ticket is closed.
                  </Text>
                  <Text style={[mib.text, { color: "#92400e", marginBottom: 8 }]}>
                    If you need further assistance, please{" "}
                    <Text
                      style={[mib.link, { color: "#d97706", fontWeight: "600", textDecorationLine: "underline" }]}
                      onPress={handleSubmitNewTicketFromClosed}
                    >
                      submit a new ticket
                    </Text>
                    .
                  </Text>
                </View>
              </View>
            ) : null}

            {/* Merged-Into banner for absorbed tickets — parentTicketRef is source of truth */}
            {isMergedClosed && ticket.parentTicketRef && showMergedBanner ? (
              <View style={[mib.banner, { marginHorizontal: 16, marginTop: 12, flexDirection: "row", justifyContent: "space-between", alignItems: "center" }]}>
                <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <MaterialIcons name="merge-type" size={18} color="#1e40af" />
                  <Text style={mib.text}>
                    {"This ticket has been merged into "}
                    <Text
                      style={mib.link}
                      onPress={() => {
                        dispatch(setSelectedTicketId(ticket.parentTicketRef!));
                        onNavigate("Ticket Detail");
                      }}
                    >
                      {ticket.parentTicketRef}
                    </Text>
                    {". All updates will be tracked there."}
                  </Text>
                </View>
                <Pressable
                  onPress={() => setShowMergedBanner(false)}
                  style={{ padding: 4, marginLeft: 8 }}
                  accessibilityLabel="Close merged ticket banner"
                >
                  <MaterialIcons name="close" size={16} color="#1e40af" />
                </Pressable>
              </View>
            ) : null}

            {/* Merge Impact Note banner - reminds team to review consolidated impact fields */}
            {ticket.mergeImpactNote ? (
              <View style={[mib.banner, { marginHorizontal: 16, marginTop: ticket.parentTicketRef ? 8 : 12, backgroundColor: "#fef3c7", borderLeftColor: "#f59e0b", borderLeftWidth: 4 }]}>
                <MaterialIcons name="info" size={18} color="#d97706" />
                <Text style={[mib.text, { color: "#92400e" }]}>
                  {ticket.mergeImpactNote}
                </Text>
              </View>
            ) : null}

            {/* Hero Card */}
            {isAccessRequestTicket && isTicketDetailLoading ? (
              <View style={md.heroCard}>
                <AccessRequestHeroSkeleton />
              </View>
            ) : (
            <View style={md.heroCard}>
              <View style={md.heroTopRow}>
                <Text style={md.heroId}>{ticket.id}</Text>
                <View style={md.heroBadges}>
                  <View style={[md.badge, { backgroundColor: S_BG[currentStatus] || "#f0f0f0" }]}>
                    <View style={[md.badgeDot, { backgroundColor: S_COLOR[currentStatus] || "#666" }]} />
                    <Text style={[md.badgeText, { color: S_COLOR[currentStatus] || "#666" }]}>{S_TEXT[currentStatus] || currentStatus}</Text>
                  </View>
                  <View style={[md.badge, { backgroundColor: U_BG[currentUrgency] || "#f0f0f0" }]}>
                    <View style={[md.badgeDot, { backgroundColor: U_COLOR[currentUrgency] || "#666" }]} />
                    <Text style={[md.badgeText, { color: U_COLOR[currentUrgency] || "#666" }]}>{currentUrgency ? toTitleCaseWord(currentUrgency) : "—"}</Text>
                  </View>
                </View>
              </View>
                <Text style={md.heroTitle}>{ticket.title}</Text>
                {isAssignedTeamInactive ? (
                  <Text style={md.heroInactiveTeamText}>Inactive Team Assignment</Text>
                ) : null}
                {ticket.reopenedFromId ? (
                  <View style={md.reopenedTagWrap}>
                    <View style={md.reopenedTagPill}>
                      <MaterialIcons name="refresh" size={12} color="#166534" />
                      <Text style={md.reopenedTagPillText}>Re-Opened Ticket</Text>
                    </View>
                    <Pressable onPress={() => handleOpenOriginalTicket(ticket.reopenedFromId!)}>
                      <Text style={md.reopenedTagLink}>{ticket.reopenedFromId}</Text>
                    </Pressable>
                  </View>
                ) : null}
              <Text style={md.heroMeta}>Submitted {formatDateWithTimezone(ticket.submitted)} by {ticket.submittedBy}</Text>
              <Text style={md.heroMeta}>Last updated {formatDateWithTimezone(ticket.lastUpdated)}</Text>
              <View style={md.heroDivider} />
              <View style={md.heroDropRow}>
                {/* STATUS */}
                <View style={md.heroDropCol}>
                  <Text style={md.heroDropLabel}>STATUS</Text>
                  <Pressable
                    ref={statusTriggerRef}
                    style={md.heroDropTrigger}
                    onPress={() => { if (canEditTriage) { openDrop("status", statusTriggerRef); } }}
                  >
                    <Text style={md.heroDropValue} numberOfLines={1}>{S_TEXT[currentStatus] || currentStatus}</Text>
                    <MaterialIcons name={canEditTriage ? "keyboard-arrow-down" : "lock"} size={18} color="#374151" />
                  </Pressable>
                </View>
                {/* URGENCY */}
                <View style={md.heroDropCol}>
                  <Text style={md.heroDropLabel}>URGENCY</Text>
                  <Pressable
                    ref={urgencyTriggerRef}
                    style={md.heroDropTrigger}
                    onPress={() => { if (canEditTriage) { openDrop("urgency", urgencyTriggerRef); } }}
                  >
                    <Text style={md.heroDropValue} numberOfLines={1}>{currentUrgency ? toTitleCaseWord(currentUrgency) : "—"}</Text>
                    <MaterialIcons name={canEditTriage ? "keyboard-arrow-down" : "lock"} size={18} color="#374151" />
                  </Pressable>
                </View>
              </View>
              {/* ASSIGNED TEAM */}
              <View style={md.heroDropRow}>
                <View style={md.heroDropCol}>
                  <Text style={md.heroDropLabel}>ASSIGNED TEAM</Text>
                  <Pressable
                    ref={assignedTeamTriggerRef}
                    style={md.heroDropTrigger}
                    onPress={() => { if (canEditTriage) { openDrop("team", assignedTeamTriggerRef); } }}
                  >
                    <Text style={md.heroDropValue} numberOfLines={1}>{assignedTeamName || "Unassigned"}</Text>
                    <MaterialIcons name={canEditTriage ? "keyboard-arrow-down" : "lock"} size={18} color="#374151" />
                  </Pressable>
                </View>
              </View>
            </View>
            )}

            {/* Tab Bar — scrolls to section */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={md.tabBar} style={md.tabBarWrap}>
              {mobileTabs.map((tab) => (
                <Pressable
                  key={tab}
                  onPress={() => scrollToSection(tab)}
                  style={({ pressed }) => [md.tab, pressed && md.tabPressed]}
                >
                  <Text style={md.tabText}>{tab}</Text>
                </Pressable>
              ))}
            </ScrollView>

            {/* All 5 sections — always visible, stacked */}
            <View
              style={md.tabContent}
              onLayout={(e) => { tabContentY.current = e.nativeEvent.layout.y; }}
            >

              {/* TRIAGE — sustain-only */}
              {canViewTriage && (
              <View
                style={{ marginBottom: 16 }}
              >
                {isAccessRequestTicket ? (
                  isTicketDetailLoading ? null : (
                  <AccessRequestTriageCard
                    ticket={ticket}
                    isReadOnly={!canEditTriage || isArTriageSubmitting}
                    isEditingDetails={isAccessRequestEditing}
                    onModifyToggle={(open) => { setIsAccessRequestEditing(open); if (!open) { setArModifyNotes(""); setArEditedFields({}); } }}
                    onModifyNotesChange={setArModifyNotes}
                    onApprove={(modifyNotes) => handleArTriage(isAccessRequestEditing ? "modify" : "approve", { modifyNotes })}
                    onModify={(notes) => handleArTriage("modify", { modifyNotes: notes })}
                    onDeny={(reason) => handleArTriage("deny", { denialReason: reason })}
                    onResolvedByIT={handleArResolveByIT}
                  />
                  )
                ) : (
                <View style={md.card}>
                  <View style={md.triageHeader}>
                    <MaterialIcons name="check-box-outline-blank" size={16} color="#7871E2" />
                    <Text style={md.triageHeaderText}>SUSTAIN TEAM TRIAGE</Text>
                  </View>
                  <View style={md.trField}>
                    <Text style={md.trLabel}>RESOLUTION PATH</Text>
                    <View style={md.trToggleRow}>
                      {RESOLUTION_PATH_MOBILE.map((opt) => (
                        <Pressable key={opt} style={[md.trToggle, resolutionPath === opt && md.trToggleActive]} onPress={() => { if (canEditTriage) { setResolutionPath(opt); } }}>
                          <Text style={[md.trToggleText, resolutionPath === opt && md.trToggleTextActive]}>{opt}</Text>
                        </Pressable>
                      ))}
                    </View>
                  </View>
                  {resolutionPath === "Requires IT" && (
                  <View style={md.trField}>
                    <Text style={md.trLabel}>SERVICENOW INCIDENT #</Text>
                    {ticket.incidentId ? (
                      /* Already linked — read-only */
                      <Text style={md.trIncident}>{ticket.incidentId}<Text style={md.trIncidentSub}>{" "}(synced)</Text></Text>
                    ) : canEditTriage ? (
                      /* Editable — accept up to 11-char ServiceNow incident number */
                      <TextInput
                        style={[md.trDropTrigger, {
                          fontSize: 14,
                          color: incidentNumber ? "#111827" : "#9ca3af",
                          fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
                          letterSpacing: 0.5,
                        }]}
                        value={incidentNumber}
                        onChangeText={(v) => setIncidentNumber(v.replace(/[^a-zA-Z0-9]/g, "").slice(0, 11))}
                        placeholder="e.g. INC0012345"
                        placeholderTextColor="#9ca3af"
                        maxLength={11}
                        autoCapitalize="characters"
                        autoCorrect={false}
                      />
                    ) : (
                      /* Read-only with no value */
                      <Text style={[md.trIncident, { color: "#9ca3af" }]}>—</Text>
                    )}
                  </View>
                  )}
                  <View style={md.trField}>
                    <Text style={md.trLabel}>ASSIGNED TO</Text>
                    <Pressable
                      ref={assignedTriggerRef}
                      style={md.trDropTrigger}
                      onPress={() => { if (canEditTriage) { openDrop("assigned", assignedTriggerRef); } }}
                    >
                      <Text style={md.trDropText} numberOfLines={1}>{assignedTo || "Unassigned"}</Text>
                      <MaterialIcons name={canEditTriage ? "keyboard-arrow-down" : "lock"} size={18} color="#374151" />
                    </Pressable>
                  </View>
                  <View style={md.trButtons}>
                    {canEditTriage && currentStatus !== "resolved" && (
                      <Pressable style={md.trBtnMerge} onPress={() => handleMerge()}>
                        <MaterialIcons name="merge-type" size={14} color="#034895" />
                        <Text style={md.trBtnMergeText}>Merge Tickets</Text>
                      </Pressable>
                    )}
                    {canEditTriage && (
                    <Pressable style={md.trBtnOutline} onPress={() => setShowCloseTicketModal(true)}>
                      <Text style={md.trBtnOutlineText}>Close Ticket</Text>
                    </Pressable>
                    )}
                    {canEditTriage && (
                    <Pressable style={md.trBtnFill} onPress={async () => { const r = await handleTriageSend(); toast.show(r.msg, r.type); }}>
                      <Text style={md.trBtnFillText}>Send Update</Text>
                    </Pressable>
                    )}
                  </View>
                </View>
                )}
              </View>
              )}

              {/* DETAILS */}
              <View
                style={{ marginBottom: 16 }}
                onLayout={(e) => { sectionOffsets.current.Details = e.nativeEvent.layout.y; }}
              >
                {isAccessRequestTicket ? (
                  isTicketDetailLoading ? (
                    <AccessRequestSkeletonDetail />
                  ) : (
                  <AccessRequestDetailSection
                    ticket={ticket}
                    isReadOnly={!canEditTriage}
                    isEditing={isAccessRequestEditing}
                    modifyNotes={arModifyNotes}
                    onEditChange={setArEditedFields}
                  />
                  )
                ) : (
                <View style={md.card}>
                  <Pressable style={md.sectionHeader} onPress={() => toggleSection("details")}>
                    <View style={md.sectionHeaderLeft}>
                      <MaterialIcons name="description" size={18} color="#374151" />
                      <View style={{ marginLeft: 8 }}>
                        <Text style={md.sectionTitle}>Submitted Details</Text>
                        <Text style={md.sectionSub}>{savedInfo.category} · {ticket.location}</Text>
                      </View>
                    </View>
                    <MaterialIcons name={collapsedSections["details"] ? "keyboard-arrow-down" : "keyboard-arrow-up"} size={22} color="#6b7280" />
                  </Pressable>
                  {!collapsedSections["details"] && <View style={md.cardDivider} />}
                  {!collapsedSections["details"] && <View style={md.sectionBody}>
                    <View style={md.dField}>
                      <Text style={md.dLabel}>SUBMITTER</Text>
                      <Text style={md.dValue}>{ticket.submittedBy}</Text>
                    </View>
                    <View style={md.dField}>
                      <Text style={md.dLabel}>CATEGORY / SUB-CATEGORY</Text>
                      <Text style={md.dValue}>{savedInfo.category} · {savedInfo.subcategory}</Text>
                    </View>
                    {savedInfo.tertiaryCategory ? (
                      <View style={md.dField}>
                        <Text style={md.dLabel}>TERTIARY CATEGORY</Text>
                        <Text style={md.dValue}>{savedInfo.tertiaryCategory}</Text>
                      </View>
                    ) : null}
                    <View style={md.dField}>
                      <Text style={md.dLabel}>TICKET LEVEL / {getLevelReferenceLabel(savedInfo.ticketLevel).toUpperCase()}</Text>
                      <Text style={md.dValue}>{savedInfo.ticketLevel}: {savedInfo.market}{ticket.sapId ? ` (SAP ID: ${ticket.sapId})` : ""}</Text>
                    </View>
                    <View style={md.dField}>
                      <Text style={md.dLabel}>NUMBER OF PEOPLE IMPACTED</Text>
                      <Text style={md.dValue}>{savedInfo.peopleImpacted}</Text>
                    </View>
                    <View style={md.dField}>
                      <Text style={md.dLabel}>PERCENT OF LOADS IMPACTED</Text>
                      <Text style={md.dValue}>{savedInfo.loadsImpacted}</Text>
                    </View>
                    <View style={md.dField}>
                      <Text style={md.dLabel}>ISSUE DESCRIPTION</Text>
                      <View style={md.descBox}><Text style={md.descText}>{savedInfo.description}</Text></View>
                    </View>
                  </View>}
                </View>
                )}
              </View>

              {/* MERGED TICKETS — shown on primary ticket after a merge */}
              {absorbedTickets.length > 0 && (
                <View style={{ marginBottom: 16 }}>
                  <MergedTicketsSection absorbed={absorbedTickets} />
                </View>
              )}

              {/* IMPACT — hidden for Access Request tickets */}
              {ticket.category !== "Access Request" && (
              <View
                style={{ marginBottom: 16 }}
                onLayout={(e) => { sectionOffsets.current.Impact = e.nativeEvent.layout.y; }}
              >
                <View style={md.card}>
                  <Pressable style={md.sectionHeader} onPress={() => toggleSection("impact")}>
                    <View style={md.sectionHeaderLeft}>
                      <MaterialIcons name="location-on" size={18} color="#374151" />
                      <View style={{ marginLeft: 8 }}>
                        <Text style={md.sectionTitle}>Impact & Affected Locations</Text>
                        <Text style={md.sectionSub}>{ticket.affectedLocations.length} locations reporting this issue</Text>
                      </View>
                    </View>
                    <MaterialIcons name={collapsedSections["impact"] ? "keyboard-arrow-down" : "keyboard-arrow-up"} size={22} color="#6b7280" />
                  </Pressable>
                  {!collapsedSections["impact"] && <View style={md.cardDivider} />}
                  {!collapsedSections["impact"] && locDisplay.map((loc, idx) => (
                    <View key={loc.id} style={[md.locRow, idx < locDisplay.length - 1 && md.locRowBorder]}>
                      <Text style={md.locName}>{loc.name}</Text>
                      <Text style={md.locTime}>{loc.time}{loc.isSubmitter ? " (submitter)" : ""}</Text>
                    </View>
                  ))}
                  {!collapsedSections["impact"] && moreCount > 0 && !showAllLocs && (
                    <View style={md.viewAllRow}>
                      <Text style={md.viewAllMore}>+ {moreCount} more locations</Text>
                      <Pressable onPress={() => setShowAllLocs(true)}><Text style={md.viewAllBtn}>View all</Text></Pressable>
                    </View>
                  )}
                  {!collapsedSections["impact"] && showAllLocs && moreCount > 0 && (
                    <View style={md.viewAllRow}>
                      <Pressable onPress={() => setShowAllLocs(false)}><Text style={md.viewAllBtn}>Show less</Text></Pressable>
                    </View>
                  )}
                </View>
              </View>
              )}

              {/* NOTES — available to all roles, hidden for Access Request tickets */}
              {canViewInternalNotes && ticket.category !== "Access Request" && (
              <View
                style={{ marginBottom: 16 }}
                onLayout={(e) => { sectionOffsets.current.Notes = e.nativeEvent.layout.y; }}
              >
                <View style={md.card}>
                  <Pressable style={md.notesHeader} onPress={() => toggleSection("notes")}>
                    <View style={md.notesHeaderLeft}>
                      <MaterialIcons name="notes" size={18} color="#374151" />
                      <Text style={md.notesTitleText}>Internal Notes</Text>
                    </View>
                    <MaterialIcons name={collapsedSections["notes"] ? "keyboard-arrow-down" : "keyboard-arrow-up"} size={22} color="#6b7280" />
                  </Pressable>
                  {!collapsedSections["notes"] && (
                    <NotesSection
                      notes={notes}
                      currentUserName="You"
                      currentUserInitials="ME"
                      currentUserAvatarColor="#7c3aed"
                      onSubmit={saveNoteFull}
                      isReadOnly={!canEditNotes}
                      hideSectionHeader
                    />
                  )}
                </View>
              </View>
              )}

              {/* ACTIVITY */}
              <View
                onLayout={(e) => { sectionOffsets.current.Activity = e.nativeEvent.layout.y; }}
              >
                <View style={md.card}>
                  <Pressable style={md.activityHeaderRow} onPress={() => toggleSection("activity")}>
                    <MaterialIcons name="schedule" size={18} color="#374151" />
                    <Text style={[md.activityTitleText, { flex: 1 }]}>Activity Stream</Text>
                    <MaterialIcons name={collapsedSections["activity"] ? "keyboard-arrow-down" : "keyboard-arrow-up"} size={22} color="#6b7280" />
                  </Pressable>
                  {!collapsedSections["activity"] && <ScrollView
                    style={md.activityListScroll}
                    contentContainerStyle={md.activityList}
                    showsVerticalScrollIndicator
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                  >
                    {activity.map((item, idx) => {
                      const isSystem = item.type === "system";
                      const isSN = item.type === "servicenow";
                      return (
                        <View key={item.id} style={md.actItem}>
                          <View style={md.actItemLeft}>
                            <View style={[md.actAvatar, { backgroundColor: ACT_AVG_BG[item.type] || "#656565" }]}>
                              <Text style={md.actAvatarText}>{item.authorInitials}</Text>
                            </View>
                            {idx < activity.length - 1 && <View style={md.actLine} />}
                          </View>
                          <View style={md.actItemRight}>
                            <View style={md.actTopRow}>
                              <Text style={md.actAuthor}>{item.authorName}</Text>
                              {item.role ? <Text style={md.actRole}>{" · "}{item.role}</Text> : null}
                              <Text style={md.actTime}>{item.time}</Text>
                            </View>
                            <View style={[md.actContent, isSN && md.actContentSN]}>
                              <Text style={[md.actContentText, isSystem && md.actContentTextSystem]}>{item.content}</Text>
                            </View>
                            {/* Activity Images */}
                            {item.images && item.images.length > 0 && (
                              <View style={md.activityImageGrid}>
                                {item.images.map((imageUrl, imgIdx) => (
                                  <View key={imgIdx} style={md.activityImageContainer}>
                                    <Image
                                      source={{ uri: imageUrl }}
                                      style={md.activityImageThumbnail}
                                      resizeMode="cover"
                                    />
                                  </View>
                                ))}
                              </View>
                            )}
                          </View>
                        </View>
                      );
                    })}

                    {canViewTriage && serviceNowEnabled ? (
                      <View style={md.snLogsWrap}>
                        <Text style={md.snLogsTitle}>ServiceNow Logs</Text>
                        {serviceNowLogsLoading ? (
                          <Text style={md.snLogsMeta}>Loading logs...</Text>
                        ) : serviceNowLogsError ? (
                          <Text style={[md.snLogsMeta, { color: "#b91c1c" }]}>{serviceNowLogsError}</Text>
                        ) : serviceNowLogs.length === 0 ? (
                          <Text style={md.snLogsMeta}>No ServiceNow log entries for this ticket yet.</Text>
                        ) : (
                          serviceNowLogs.slice(0, 5).map((log) => (
                            <View key={log.id} style={md.snLogRow}>
                              <View style={{ flex: 1 }}>
                                <Text style={md.snLogOp}>{log.operation}</Text>
                                <Text style={md.snLogMsg} numberOfLines={2}>
                                  {log.errorMessage || log.errorCode || "No error message"}
                                </Text>
                              </View>
                              <View style={{ alignItems: "flex-end", marginLeft: 12 }}>
                                <Text style={md.snLogStatus}>{log.status}</Text>
                                <Text style={md.snLogRetry}>Retry {log.retryCount}</Text>
                              </View>
                            </View>
                          ))
                        )}
                      </View>
                    ) : null}
                  </ScrollView>}
                  {!collapsedSections["activity"] && <View style={md.commentWrap}>
                    {isClosed ? (
                      <View style={md.commentWrap}>
                        <Text style={[md.commentInput, { color: "#9ca3af" }]}>This ticket has been resolved / closed and is read-only.</Text>
                      </View>
                    ) : (
                    <TextInput
                      style={md.commentInput}
                      value={commentText}
                      onChangeText={setCommentText}
                      placeholder="Add a comment. Sustain Team comments will sync to ServiceNow within 5 minutes."
                      placeholderTextColor="#9099aa"
                      multiline
                      numberOfLines={3}
                      textAlignVertical="top"
                    />
                    )}
                    {/* Activity Image Previews */}
                    {activityImages.length > 0 && !isClosed && (
                      <View style={md.imagePreviewContainer}>
                        <Text style={md.imagePreviewLabel}>Attached Images ({activityImages.length}/3)</Text>
                        <View style={md.imagePreviewGrid}>
                          {activityImages.map((imageUri, index) => (
                            <View key={index} style={md.imagePreviewItem}>
                              <Image
                                source={{ uri: activityImageUrls[index] || imageUri }}
                                style={md.imagePreviewImage}
                              />
                              <Pressable
                                style={md.imageRemoveBtn}
                                onPress={() => handleRemoveActivityImage(index)}
                              >
                                <MaterialIcons name="close" size={14} color="#ffffff" />
                              </Pressable>
                            </View>
                          ))}
                        </View>
                      </View>
                    )}
                    {!isClosed && <View style={md.commentFooter}>
                      <Pressable
                        style={[md.imagePickerBtn, activityImages.length >= 3 && md.btnDisabled]}
                        onPress={handlePickActivityImage}
                        disabled={activityImages.length >= 3}
                      >
                        <MaterialIcons name="add-a-photo" size={16} color="#034895" />
                        <Text style={md.imagePickerBtnText}>Add Image</Text>
                      </Pressable>
                      <Pressable
                        style={[md.postBtn, !commentText.trim() && activityImages.length === 0 && md.btnDisabled]}
                        onPress={handlePostActivityWithImages}
                        disabled={!commentText.trim() && activityImages.length === 0}
                      >
                        <Text style={md.postBtnText}>Post</Text>
                      </Pressable>
                    </View>}
                  </View>}
                </View>
              </View>

            </View>
          </ScrollView>
        </View>
        {actionModals}
        {reopenConfirmModal}
        {teamReassignConfirmModal}
      </View>
    );
  }

  /* ── Web / iPad Layout (unchanged) ──────────────────────────────────────── */

  /** Banner shown on absorbed tickets linking back to the primary.
   * parentTicketRef (e.g. "NTI-2026-00366") is the sole source of truth for
   * both visibility, display text, and navigation. The banner is hidden when
   * parentTicketRef is absent.
   */
  const mergedIntoBanner = isMergedClosed && ticket.parentTicketRef && showMergedBanner ? (
    <View style={[mib.banner, { flexDirection: "row", justifyContent: "space-between", alignItems: "center" }]}>
      <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 8 }}>
        <MaterialIcons name="merge-type" size={18} color="#1e40af" />
        <Text style={mib.text}>
          {"This ticket has been merged into "}
          <Text
            style={mib.link}
            onPress={() => {
              dispatch(setSelectedTicketId(ticket.parentTicketRef!));
              onNavigate("Ticket Detail");
            }}
          >
            {ticket.parentTicketRef}
          </Text>
          {". All updates will be tracked there."}
        </Text>
      </View>
      <Pressable
        onPress={() => setShowMergedBanner(false)}
        style={{ padding: 4, marginLeft: 8 }}
        accessibilityLabel="Close merged ticket banner"
      >
        <MaterialIcons name="close" size={16} color="#1e40af" />
      </Pressable>
    </View>
  ) : null;

  return (
    <View style={styles.screenRoot}>
      <View style={styles.container}>
        <ScrollView ref={scrollViewRef} style={styles.content} contentContainerStyle={styles.scrollContent}>
          <TicketDetailHeader
            ticketId={ticket.id}
            sourceScreen={ticketDetailSource}
            onBackToSource={() => onNavigate(ticketDetailSource === "Homepage Banner" ? "Ticketing Portal" : ticketDetailSource)}
            onBackToPortal={() => onNavigate("Ticketing Portal")}
            onCopyLink={() => {
              // Build deep link URL: "Ticket Detail?ticketId=NTI-2026-00022"
              const ticketIdParam = ticket.id;
              const deepLink = `Ticket Detail?ticketId=${encodeURIComponent(ticketIdParam)}`;
              const webPath = `/ticket-details/ticket-id/${encodeURIComponent(ticketIdParam)}`;

              // Build clean URL on web when available; fallback to hash-based deep link.
              const fullUrl = Platform.OS === 'web' && typeof window !== 'undefined'
                ? `${window.location.origin}${webPath}`
                : deepLink;

              // Copy to clipboard
              Clipboard.setString(fullUrl);
              toast.show("Link copied to clipboard", "success");
            }}
            onReopen={canReopen ? () => setShowReopenConfirm(true) : undefined}
          />
          {mergedIntoBanner}
          <View style={styles.detailLayout}>
            <View style={styles.detailMain}>
              {ticket.status === "resolved" ? (
                <View style={[styles.closedBanner, { marginBottom: 16 }]}>
                  <MaterialIcons name="lock" size={16} color="#d97706" />
                  <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 4 }}>
                    <Text style={styles.closedBannerText}>This ticket is closed. Need further assistance?</Text>
                    <Text
                      style={styles.closedBannerLink}
                      onPress={handleSubmitNewTicketFromClosed}
                    >
                      Submit a new ticket
                    </Text>
                  </View>
                </View>
              ) : null}
              <View style={{ zIndex: 50 }}>
                {isAccessRequestTicket && isTicketDetailLoading ? (
                  <AccessRequestHeroSkeleton />
                ) : (
                <TicketHero
                  ticket={ticket}
                  savedInfo={savedInfo}
                  isWidespread={isWidespread}
                  isReadOnly={!canEditTriage}
                  canAccessServiceNow={serviceNowEnabled && isSustainTeamMember}
                  onStatusChange={canEditTriage ? handleStatusChange : undefined}
                  onOpenReopenedTicket={handleOpenOriginalTicket}
                  assignedTeams={assignedTeams}
                  assignedTeamName={assignedTeamName}
                  isAssignedTeamInactive={isAssignedTeamInactive}
                  onTeamChange={canEditTriage ? async (teamId) => {
                    const r = await changeAssignedTeam(teamId);
                    toast.show(r.msg, r.type);
                  } : undefined}
                  onToggleWidespread={canEditTriage ? () => {
                    const r = toggleWidespread();
                    if (r) toast.show(r.msg, r.type);
                  } : undefined}
                />
                )}
              </View>
              {canViewTriage && (
              <View style={{ zIndex: 40 }}>
                {shouldShowServiceNowErrorBanner ? (
                  <ServiceNowErrorBanner
                    status={serviceNowStatus!}
                    onRetryNow={handleServiceNowRetryNow}
                    retryEnabled={!isServiceNowRetrying && !!ticket.backendTicketId}
                  />
                ) : null}
                {isAccessRequestTicket ? (
                  isTicketDetailLoading ? null : (
                  <AccessRequestTriageCard
                    ticket={ticket}
                    isReadOnly={!canEditTriage || isArTriageSubmitting}
                    isEditingDetails={isAccessRequestEditing}
                    onModifyToggle={(open) => { setIsAccessRequestEditing(open); if (!open) { setArModifyNotes(""); setArEditedFields({}); } }}
                    onModifyNotesChange={setArModifyNotes}
                    onApprove={(modifyNotes) => handleArTriage(isAccessRequestEditing ? "modify" : "approve", { modifyNotes })}
                    onModify={(notes) => handleArTriage("modify", { modifyNotes: notes })}
                    onDeny={(reason) => handleArTriage("deny", { denialReason: reason })}
                    onResolvedByIT={handleArResolveByIT}
                  />
                  )
                ) : (
                <TriageCard 
                  ticket={ticket}
                  activity={activity}
                  resolutionPath={resolutionPath} 
                  assignedTo={assignedTo}
                  incidentNumber={incidentNumber}
                  canAccessServiceNow={serviceNowEnabled && isSustainTeamMember}
                  assignedToOptions={memberNameOptions} 
                  onResolutionPathChange={setResolutionPath} 
                  onAssignedToChange={setAssignedTo}
                  onIncidentNumberChange={setIncidentNumber}
                  onMerge={() => handleMerge()} 
                  onCreateADO={() => handleCreateADO()} 
                  onManageImpact={() => {
                    if (!affectedLocationsRef.current) return;
                    if (Platform.OS === "web") {
                      // On web, the RN View ref is a DOM element — use native scrollIntoView
                      (affectedLocationsRef.current as any).scrollIntoView({ behavior: "smooth", block: "start" });
                    } else {
                      // On native, measure position relative to the ScrollView
                      affectedLocationsRef.current.measureLayout(
                        scrollViewRef.current as any,
                        (_x: number, y: number) => { scrollViewRef.current?.scrollTo({ y, animated: true }); },
                        () => { scrollViewRef.current?.scrollTo({ y: affectedLocationsY, animated: true }); }
                      );
                    }
                  }} 
                  onResolve={() => setShowCloseTicketModal(true)} 
                  onSendUpdate={async () => { const r = await handleTriageSend(); toast.show(r.msg, r.type); return r; }}
                  hasChanges={hasTriageChanges()}
                  isSending={isTriageSending}
                  isReadOnly={!canEditTriage} 
                />
                )}
              </View>
              )}
              {isAccessRequestTicket ? (
              <View style={{ zIndex: 35 }}>
                {isTicketDetailLoading ? (
                  <AccessRequestSkeletonDetail />
                ) : (
                  <AccessRequestDetailSection
                    ticket={ticket}
                    isReadOnly={!canEditTriage}
                    isEditing={isAccessRequestEditing}
                    modifyNotes={arModifyNotes}
                    onEditChange={setArEditedFields}
                  />
                )}
              </View>
              ) : null}
              <View style={{ zIndex: 30 }}>
                <TicketInfoSection ticket={ticket} savedInfo={savedInfo} isEditing={isEditingInfo} draft={editDraft} editErrors={editErrors} onStartEdit={startEdit} onCancelEdit={cancelEdit} onSaveEdit={(dynVals) => { const r = saveEdit(dynVals); toast.show(r.msg, r.type); }} onPatchDraft={patchDraft} isReadOnly={!canEditSubmittedDetails} stakeholdersReadOnly={!canEditTriage} onAddStakeholder={addSecondaryStakeholder} />
              </View>
              {absorbedTickets.length > 0 && (
              <View style={{ zIndex: 25 }}>
                <MergedTicketsSection absorbed={absorbedTickets} />
              </View>
              )}
              {!isAccessRequestTicket && (
              <View style={{ zIndex: 20 }} ref={affectedLocationsRef} onLayout={(e) => setAffectedLocationsY(e.nativeEvent.layout.y)}>
                <AffectedLocationsSection ticket={ticket} locationsToShow={locationsToShow} impactedPickersCount={impactedPickersCount} hasMoreLocations={hasMoreLocations} showAll={showAllLocations} onToggleShowAll={setShowAllLocations} onAddLocation={addImpactLocation} onRemoveLocation={removeImpactLocation} isReadOnly={!canEditTriage} />
              </View>
              )}
              {canViewInternalNotes && !isAccessRequestTicket && (
              <View style={{ zIndex: 10 }}>
                <NotesSection notes={notes} currentUserName="You" currentUserInitials="ME" currentUserAvatarColor="#7c3aed" onSubmit={saveNoteFull} isReadOnly={!canEditNotes} />
              </View>
              )}
            </View>
            <View style={styles.detailSide}>
              <ActivityStream
                activity={activity}
                currentUserName="You"
                currentUserInitials="ME"
                onSubmit={(text, images) => postCommentFull(text, images, actorRoleTag ?? undefined)}
                isReadOnly={isClosed || !canPostComment}
                hideServiceNowSection={isAccessRequestTicket}
                showServiceNowLogs={canViewTriage}
                serviceNowLogs={serviceNowLogs}
                serviceNowLogsLoading={serviceNowLogsLoading}
                serviceNowLogsError={serviceNowLogsError}
              />
            </View>
          </View>
        </ScrollView>
      </View>
      {actionModals}
      {reopenConfirmModal}
      {teamReassignConfirmModal}
      <CloseTicketModal
        visible={showCloseTicketModal}
        onClose={() => {
          setShowCloseTicketModal(false);
          setArTriageFailureError(""); // Clear any pending approval error
        }}
        ticketId={ticket.id}
        ticketCategory={ticket.category}
        ticketSubCategory={ticket.subcategory}
        categoryConfig={categoryConfig}
        onSubmit={arTriageFailureError.trim() ? handleArTriageFailureRca : handleCloseTicket}
      />
      {serviceNowEnabled && (
        <ServiceNowIncidentModal
          visible={showServiceNowModal}
          ticketId={ticket.backendTicketId}
          existingIncidentNumber={incidentNumber}
          onClose={() => setShowServiceNowModal(false)}
          onProceed={handleServiceNowProceed}
          isSubmitting={isServiceNowSubmitting}
        />
      )}
    </View>
  );
};

/* ── Mobile-only styles ───────────────────────────────────────────────────── */
const md = StyleSheet.create({
  root: { flex: 1, backgroundColor: "#f0f2f7" },
  // Header
  header: { flexDirection: "row", alignItems: "center", backgroundColor: "#ffffff", paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: "#e5e7eb", gap: 12 },
  backBtn: { padding: 4 },
  headerTitle: { fontSize: 18, fontWeight: "700", color: "#111827" },
  headerSub: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  // Hero card
  heroCard: { backgroundColor: "#ffffff", marginHorizontal: 16, marginTop: 16, borderRadius: 12, padding: 16, shadowColor: "#000", shadowOpacity: 0.06, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 2 },
  heroTopRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 6 },
  heroId: { fontSize: 14, fontWeight: "700", color: "#034895", fontFamily: Platform.OS === "ios" ? "Courier" : "monospace" },
  heroBadges: { flexDirection: "row", gap: 6, flexWrap: "wrap" },
  badge: { flexDirection: "row", alignItems: "center", gap: 5, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  badgeDot: { width: 7, height: 7, borderRadius: 4 },
  badgeText: { fontSize: 12, fontWeight: "600" },
  heroTitle: { fontSize: 18, fontWeight: "700", color: "#111827", lineHeight: 26, marginBottom: 8 },
  heroInactiveTeamText: { fontSize: 12, fontWeight: "700", color: "#C12C01", marginBottom: 8 },
  heroMeta: { fontSize: 13, color: "#6b7280", marginBottom: 2, lineHeight: 19 },
  heroDivider: { height: 1, backgroundColor: "#e5e7eb", marginVertical: 14 },
  heroDropRow: { flexDirection: "row", gap: 12 },
  heroDropCol: { flex: 1 },
  heroDropLabel: { fontSize: 11, fontWeight: "700", color: "#6b7280", letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6 },
  heroDropTrigger: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", borderWidth: 1, borderColor: "#d1d5db", borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, backgroundColor: "#ffffff" },
  heroDropValue: { fontSize: 14, fontWeight: "600", color: "#111827", flex: 1 },
  // Dropdown overlay (rendered in Modal, floats above all content)
  overlayDropMenu: {
    position: "absolute",
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 10,
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 24,
    overflow: "scroll" as any,
    maxHeight: 300,
    ...Platform.select({
      web: {
        overflowY: "auto",
        overflowX: "hidden",
      } as any,
    }),
  },
  dropItem: { paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: "#f0f0f0" },
  dropItemActive: { backgroundColor: "#eaf0fb" },
  dropItemText: { fontSize: 14, color: "#374151" },
  dropItemTextActive: { color: "#034895", fontWeight: "700" },
  // Tab bar
  tabBarWrap: { backgroundColor: "#ffffff", marginTop: 14, borderBottomWidth: 1, borderBottomColor: "#e5e7eb" },
  tabBar: { flexDirection: "row", paddingHorizontal: 12, paddingVertical: 10, gap: 8 },
  tab: { paddingHorizontal: 18, paddingVertical: 9, borderRadius: 24, backgroundColor: "#EEF2FF", borderWidth: 1, borderColor: "#C7D2FE" },
  tabPressed: { backgroundColor: "#E0E7FF", borderColor: "#A5B4FC", opacity: 0.85 },
  tabText: { fontSize: 14, fontWeight: "600", color: "#1D4ED8" },
  tabContent: { padding: 16 },
  // Shared card
  card: { backgroundColor: "#ffffff", borderRadius: 12, overflow: "hidden", shadowColor: "#000", shadowOpacity: 0.06, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 2 },
  cardDivider: { height: 1, backgroundColor: "#e5e7eb" },
  // Shared section header
  sectionHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: 16 },
  sectionHeaderLeft: { flexDirection: "row", alignItems: "center" },
  sectionTitle: { fontSize: 16, fontWeight: "700", color: "#111827" },
  sectionSub: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  sectionBody: { padding: 16, gap: 16 },
  // Triage
  triageHeader: { flexDirection: "row", alignItems: "center", gap: 8, backgroundColor: "#F0EFFE", paddingHorizontal: 16, paddingVertical: 13 },
  triageHeaderText: { fontSize: 12, fontWeight: "700", color: "#7871E2", letterSpacing: 1, textTransform: "uppercase" },
  trField: { paddingHorizontal: 16, paddingVertical: 14, borderTopWidth: 1, borderTopColor: "#f0f0f0" },
  trLabel: { fontSize: 11, fontWeight: "700", color: "#6b7280", letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 10 },
  trToggleRow: { flexDirection: "row", gap: 10 },
  trToggle: { flex: 1, paddingVertical: 11, borderRadius: 24, borderWidth: 1.5, borderColor: "#d1d5db", alignItems: "center" },
  trToggleActive: { backgroundColor: "#034895", borderColor: "#034895" },
  trToggleText: { fontSize: 14, fontWeight: "600", color: "#374151" },
  trToggleTextActive: { color: "#ffffff" },
  trIncident: { fontSize: 15, fontWeight: "700", color: "#034895", lineHeight: 22 },
  trIncidentSub: { fontSize: 13, fontWeight: "400", color: "#6b7280" },
  trEmpty: { fontSize: 15, color: "#9ca3af" },
  trDropTrigger: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", borderWidth: 1, borderColor: "#d1d5db", borderRadius: 8, paddingHorizontal: 14, paddingVertical: 12, backgroundColor: "#ffffff" },
  trDropText: { fontSize: 14, color: "#111827", flex: 1 },
  trButtons: { flexDirection: "row", flexWrap: "wrap", gap: 10, paddingHorizontal: 16, paddingVertical: 16, borderTopWidth: 1, borderTopColor: "#f0f0f0" },
  trBtnMerge: { flexDirection: "row", alignItems: "center", gap: 6, borderWidth: 1.5, borderColor: "#034895", borderRadius: 24, paddingVertical: 10, paddingHorizontal: 16 },
  trBtnMergeText: { fontSize: 13, fontWeight: "700", color: "#034895" },
  trBtnOutline: { flex: 1, borderWidth: 1.5, borderColor: "#034895", borderRadius: 24, paddingVertical: 12, alignItems: "center" },
  trBtnOutlineText: { fontSize: 14, fontWeight: "700", color: "#034895" },
  trBtnFill: { flex: 1, backgroundColor: "#034895", borderRadius: 24, paddingVertical: 12, alignItems: "center" },
  trBtnFillText: { fontSize: 14, fontWeight: "700", color: "#ffffff" },
  // Details fields
  dField: { gap: 5 },
  dLabel: { fontSize: 11, fontWeight: "700", color: "#6b7280", letterSpacing: 0.6, textTransform: "uppercase" },
  dValue: { fontSize: 15, color: "#111827", lineHeight: 22 },
  descBox: { backgroundColor: "#f8f9fa", borderRadius: 8, padding: 12, borderWidth: 1, borderColor: "#e5e7eb", marginTop: 4 },
  descText: { fontSize: 14, color: "#374151", lineHeight: 22 },
  // Impact
  locRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 16, paddingVertical: 13 },
  locRowBorder: { borderBottomWidth: 1, borderBottomColor: "#f0f0f0" },
  locName: { fontSize: 14, color: "#111827", flex: 1 },
  locTime: { fontSize: 13, color: "#6b7280", marginLeft: 8 },
  viewAllRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 16, paddingVertical: 14, borderTopWidth: 1, borderTopColor: "#f0f0f0" },
  viewAllMore: { fontSize: 14, color: "#6b7280" },
  viewAllBtn: { fontSize: 14, fontWeight: "700", color: "#034895" },
  // Notes
  notesHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: 16, borderBottomWidth: 0 },
  notesHeaderLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
  notesTitleText: { fontSize: 16, fontWeight: "700", color: "#111827" },
  sustainBadge: { backgroundColor: "#FFF3CD", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 },
  sustainBadgeText: { fontSize: 10, fontWeight: "700", color: "#856404", letterSpacing: 0.5 },
  noteComposerWrap: { padding: 16, backgroundColor: "#FFFDF0", borderBottomWidth: 1, borderBottomColor: "#e5e7eb" },
  noteInput: { borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, padding: 12, fontSize: 14, color: "#111827", backgroundColor: "#ffffff", minHeight: 80 },
  saveNoteBtn: { marginTop: 10, backgroundColor: "#1a2332", borderRadius: 8, paddingVertical: 13, alignItems: "center" },
  saveNoteBtnText: { color: "#ffffff", fontSize: 15, fontWeight: "700" },
  noteItem: { marginHorizontal: 16, marginVertical: 12, borderLeftWidth: 3, borderLeftColor: "#F59E0B", paddingLeft: 12 },
  noteItemHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  noteItemLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
  noteAvatar: { width: 32, height: 32, borderRadius: 16, alignItems: "center", justifyContent: "center" },
  noteAvatarText: { color: "#ffffff", fontSize: 11, fontWeight: "700" },
  noteAuthor: { fontSize: 14, fontWeight: "700", color: "#111827" },
  noteRole: { fontSize: 12, color: "#6b7280" },
  noteTime: { fontSize: 12, color: "#9ca3af" },
  noteBody: { fontSize: 14, color: "#374151", lineHeight: 21 },
  // Activity
  activityHeaderRow: { flexDirection: "row", alignItems: "center", gap: 8, padding: 16, borderBottomWidth: 0 },
  activityTitleText: { fontSize: 16, fontWeight: "700", color: "#111827" },
  activityListScroll: { maxHeight: 420 },
  activityList: { paddingHorizontal: 16, paddingTop: 10, paddingBottom: 10 },
  actItem: { flexDirection: "row", gap: 12 },
  actItemLeft: { alignItems: "center", width: 36 },
  actAvatar: { width: 36, height: 36, borderRadius: 18, alignItems: "center", justifyContent: "center" },
  actAvatarText: { color: "#ffffff", fontSize: 11, fontWeight: "700" },
  actLine: { width: 1.5, flex: 1, backgroundColor: "#e5e7eb", marginTop: 2, minHeight: 16 },
  actItemRight: { flex: 1, paddingBottom: 20 },
  actTopRow: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", marginBottom: 6, gap: 2 },
  actAuthor: { fontSize: 14, fontWeight: "700", color: "#111827" },
  actRole: { fontSize: 13, color: "#6b7280" },
  actTime: { fontSize: 12, color: "#9ca3af", marginLeft: "auto" as any },
  actContent: { backgroundColor: "#f3f4f6", borderRadius: 8, padding: 12 },
  actContentSN: { backgroundColor: "#DAEEFA" },
  actContentText: { fontSize: 14, color: "#374151", lineHeight: 21 },
  actContentTextSystem: { fontStyle: "italic", color: "#6b7280" },
  activityImageGrid: { flexDirection: "row", gap: 12, flexWrap: "wrap", marginTop: 12 },
  activityImageContainer: { width: 140, height: 140, borderRadius: 12, overflow: "hidden" as any, backgroundColor: "#e5e7eb", shadowColor: "#000", shadowOpacity: 0.1, shadowRadius: 4, shadowOffset: { width: 0, height: 2 }, elevation: 2 },
  activityImageThumbnail: { width: "100%", height: "100%", borderRadius: 12 },
  snLogsWrap: { marginTop: 14, borderTopWidth: 1, borderTopColor: "#e5e7eb", paddingTop: 12 },
  snLogsTitle: { fontSize: 14, fontWeight: "700", color: "#1f2937", marginBottom: 8 },
  snLogsMeta: { fontSize: 13, color: "#6b7280" },
  snLogRow: { flexDirection: "row", justifyContent: "space-between", gap: 10, backgroundColor: "#f8fafc", borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, padding: 10, marginBottom: 8 },
  snLogOp: { fontSize: 13, fontWeight: "700", color: "#1f2937", marginBottom: 2 },
  snLogMsg: { fontSize: 12, color: "#4b5563" },
  snLogStatus: { fontSize: 12, fontWeight: "700", color: "#1d4ed8" },
  snLogRetry: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  commentWrap: { padding: 16, borderTopWidth: 1, borderTopColor: "#f0f0f0" },
  commentInput: { borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, padding: 12, fontSize: 14, color: "#111827", backgroundColor: "#ffffff", minHeight: 70 },
  commentFooter: { flexDirection: "row", justifyContent: "flex-end", gap: 8, marginTop: 8, alignItems: "center" },
  postBtn: { backgroundColor: "#034895", borderRadius: 8, paddingHorizontal: 20, paddingVertical: 10 },
  postBtnText: { color: "#ffffff", fontSize: 14, fontWeight: "700" },
  imagePickerBtn: { flexDirection: "row", alignItems: "center", gap: 6, borderWidth: 1.5, borderColor: "#034895", borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
  imagePickerBtnText: { fontSize: 13, fontWeight: "600", color: "#034895" },
  imagePreviewContainer: { paddingHorizontal: 16, paddingVertical: 12, backgroundColor: "#f8fafc", borderTopWidth: 1, borderTopColor: "#e5e7eb", borderBottomWidth: 1, borderBottomColor: "#e5e7eb" },
  imagePreviewLabel: { fontSize: 12, fontWeight: "600", color: "#6b7280", marginBottom: 8 },
  imagePreviewGrid: { flexDirection: "row", gap: 8, flexWrap: "wrap" },
  imagePreviewItem: { position: "relative" as any, width: 40, height: 40 },
  imagePreviewImage: { width: 40, height: 40, borderRadius: 8, backgroundColor: "#e5e7eb" },
  imageRemoveBtn: { position: "absolute" as any, top: 2, right: 2, backgroundColor: "#ef4444", borderRadius: 12, width: 24, height: 24, alignItems: "center", justifyContent: "center" },
  btnDisabled: { opacity: 0.45 },
  // Re-open button (mobile header)
  reopenBtn: { flexDirection: "row", alignItems: "center", gap: 4, backgroundColor: "#16a34a", borderRadius: 8, paddingHorizontal: 10, paddingVertical: 7 },
  reopenBtnText: { color: "#ffffff", fontSize: 12, fontWeight: "700" },
  reopenedTagWrap: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 },
  reopenedTagPill: { flexDirection: "row", alignItems: "center", gap: 4, backgroundColor: "#DCFCE7", borderRadius: 999, paddingHorizontal: 8, paddingVertical: 4 },
  reopenedTagPillText: { color: "#166534", fontSize: 11, fontWeight: "700" },
  reopenedTagLink: { color: "#034895", fontSize: 12, fontWeight: "700", textDecorationLine: "underline" },
});

/* ── Re-open confirm dialog styles ──────────────────────────────────────── */
const reo = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.45)", alignItems: "center", justifyContent: "center", padding: 24 },
  dialog: { backgroundColor: "#ffffff", borderRadius: 14, padding: 24, width: "100%", maxWidth: 400, shadowColor: "#000", shadowOpacity: 0.18, shadowRadius: 24, shadowOffset: { width: 0, height: 8 }, elevation: 24 },
  dialogHeader: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 14 },
  dialogTitle: { fontSize: 18, fontWeight: "700", color: "#111827" },
  dialogBody: { fontSize: 14, color: "#374151", lineHeight: 22, marginBottom: 24 },
  dialogActions: { flexDirection: "row", gap: 10, justifyContent: "flex-end" },
  btnCancel: { paddingHorizontal: 18, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: "#d1d5db", backgroundColor: "#ffffff" },
  btnCancelText: { fontSize: 14, fontWeight: "600", color: "#374151" },
  btnReopen: { flexDirection: "row", alignItems: "center", gap: 6, paddingHorizontal: 18, paddingVertical: 10, borderRadius: 8, backgroundColor: "#16a34a" },
  btnReopenText: { fontSize: 14, fontWeight: "700", color: "#ffffff" },
});

/** Styles for the Merged-Into banner shown on absorbed tickets */
const mib = StyleSheet.create({
  banner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    backgroundColor: "#DBEAFE",
    borderWidth: 1,
    borderColor: "#93C5FD",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 0,
    marginBottom: 12,
  },
  text: {
    flex: 1,
    fontSize: 14,
    color: "#1e3a5f",
    lineHeight: 21,
    fontWeight: "500",
  },
  link: {
    color: "#1d4ed8",
    fontWeight: "700",
    textDecorationLine: "underline",
  },
});

export default TicketDetailScreen;
